import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { staffScheduleService } from "@/services/staffScheduleService";
import {
  CreateStaffScheduleDto,
  UpdateStaffScheduleDto,
  ReviewDayOffRequestDto,
  CreateDayOffRequestDto,
} from "@/types/staff-schedule";

export const staffScheduleKeys = {
  all: ["staffSchedules"] as const,
  lists: () => [...staffScheduleKeys.all, "list"] as const,
  list: (hotelId: string) => [...staffScheduleKeys.lists(), hotelId] as const,
  details: () => [...staffScheduleKeys.all, "detail"] as const,
  detail: (id: string) => [...staffScheduleKeys.details(), id] as const,
  availability: () => [...staffScheduleKeys.all, "availability"] as const,
  availabilityByHotel: (hotelId: string, date: string) =>
    [...staffScheduleKeys.availability(), hotelId, date] as const,
  availabilityRange: (hotelId: string, startDate: string, endDate: string) =>
    [
      ...staffScheduleKeys.availability(),
      "range",
      hotelId,
      startDate,
      endDate,
    ] as const,
  mySchedules: (hotelId: string) =>
    [...staffScheduleKeys.all, "my", hotelId] as const,
  stats: (hotelId: string, date: string) =>
    [...staffScheduleKeys.all, "stats", hotelId, date] as const,
};

export const dayOffKeys = {
  all: ["dayOffRequests"] as const,
  lists: () => [...dayOffKeys.all, "list"] as const,
  list: (hotelId: string, status?: string) =>
    [...dayOffKeys.lists(), hotelId, status] as const,
  details: () => [...dayOffKeys.all, "detail"] as const,
  detail: (id: string) => [...dayOffKeys.details(), id] as const,
  my: () => [...dayOffKeys.all, "my"] as const,
};

// --- Schedule Hooks ---

export const useStaffSchedules = (hotelId: string) => {
  return useQuery({
    queryKey: staffScheduleKeys.list(hotelId),
    queryFn: () => staffScheduleService.getSchedulesByHotel(hotelId),
    staleTime: 5 * 60 * 1000,
    enabled: !!hotelId,
  });
};

export const useStaffScheduleDetail = (id: string) => {
  return useQuery({
    queryKey: staffScheduleKeys.detail(id),
    queryFn: () => staffScheduleService.getScheduleById(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  });
};

export const useCreateStaffSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStaffScheduleDto) =>
      staffScheduleService.createSchedule(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.list(variables.hotelId),
      });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.availability(),
      });
      queryClient.invalidateQueries({
        queryKey: [...staffScheduleKeys.all, "stats"],
      });
    },
  });
};

export const useUpdateStaffSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      hotelId: string;
      data: UpdateStaffScheduleDto;
    }) => staffScheduleService.updateSchedule(id, data),
    onSuccess: (updatedSchedule, { hotelId }) => {
      queryClient.setQueryData(
        staffScheduleKeys.detail(updatedSchedule.id),
        updatedSchedule,
      );
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.list(hotelId),
      });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.availability(),
      });
      queryClient.invalidateQueries({
        queryKey: [...staffScheduleKeys.all, "stats"],
      });
    },
  });
};

export const useDeleteStaffSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; hotelId: string }) =>
      staffScheduleService.deleteSchedule(id),
    onSuccess: (_, { id, hotelId }) => {
      queryClient.removeQueries({
        queryKey: staffScheduleKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.list(hotelId),
      });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.availability(),
      });
      queryClient.invalidateQueries({
        queryKey: [...staffScheduleKeys.all, "stats"],
      });
    },
  });
};

// --- Availability Hooks ---

export const useStaffAvailability = (hotelId: string, date: string) => {
  return useQuery({
    queryKey: staffScheduleKeys.availabilityByHotel(hotelId, date),
    queryFn: () => staffScheduleService.getAvailabilityByHotel(hotelId, date),
    staleTime: 0,
    enabled: !!hotelId && !!date,
  });
};

export const useStaffAvailabilityRange = (
  hotelId: string,
  startDate: string,
  endDate: string,
) => {
  return useQuery({
    queryKey: staffScheduleKeys.availabilityRange(hotelId, startDate, endDate),
    queryFn: () =>
      staffScheduleService.getAvailabilityRange(hotelId, startDate, endDate),
    staleTime: 0,
    enabled: !!hotelId && !!startDate && !!endDate,
  });
};

// --- Stats Hook ---

export const useScheduleStats = (hotelId: string, date: string) => {
  return useQuery({
    queryKey: staffScheduleKeys.stats(hotelId, date),
    queryFn: () => staffScheduleService.getStats(hotelId, date),
    staleTime: 5 * 60 * 1000,
    enabled: !!hotelId && !!date,
  });
};

// --- Day-Off Hooks ---

export const useDayOffRequests = (hotelId: string, status?: string) => {
  return useQuery({
    queryKey: dayOffKeys.list(hotelId, status),
    queryFn: () =>
      staffScheduleService.getDayOffRequestsByHotel(hotelId, status),
    staleTime: 5 * 60 * 1000,
    enabled: !!hotelId,
  });
};

export const useCreateDayOffRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDayOffRequestDto) =>
      staffScheduleService.createDayOffRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dayOffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dayOffKeys.my() });
    },
  });
};

export const useReviewDayOffRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReviewDayOffRequestDto }) =>
      staffScheduleService.reviewDayOffRequest(id, data),
    onSuccess: (updatedRequest) => {
      queryClient.setQueryData(
        dayOffKeys.detail(updatedRequest.id),
        updatedRequest,
      );
      queryClient.invalidateQueries({ queryKey: dayOffKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.availability(),
      });
      queryClient.invalidateQueries({
        queryKey: [...staffScheduleKeys.all, "stats"],
      });
    },
  });
};

export const useCancelDayOffRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => staffScheduleService.cancelDayOffRequest(id),
    onSuccess: (updatedRequest) => {
      queryClient.setQueryData(
        dayOffKeys.detail(updatedRequest.id),
        updatedRequest,
      );
      queryClient.invalidateQueries({ queryKey: dayOffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dayOffKeys.my() });
      queryClient.invalidateQueries({
        queryKey: staffScheduleKeys.availability(),
      });
      queryClient.invalidateQueries({
        queryKey: [...staffScheduleKeys.all, "stats"],
      });
    },
  });
};

// --- My Schedule Hooks (Staff self-service) ---

export const useMySchedules = (hotelId: string) => {
  return useQuery({
    queryKey: staffScheduleKeys.mySchedules(hotelId),
    queryFn: () => staffScheduleService.getMySchedules(hotelId),
    staleTime: 5 * 60 * 1000,
    enabled: !!hotelId,
  });
};

export const useMyDayOffRequests = () => {
  return useQuery({
    queryKey: dayOffKeys.my(),
    queryFn: () => staffScheduleService.getMyDayOffRequests(),
    staleTime: 5 * 60 * 1000,
  });
};
